import React from 'react';
import PropTypes from 'prop-types';

import { colorList, fontSizeList, fontWeightList } from '../../../typings/design-tokens';
import { IComponent } from '../../../typings/generic-component';
import { makeClassNames, IClassNames } from '../../../utils/styles';
import { objectToDataAttributes } from '../../../utils/tracking-data';

import './text.scss';

export type TextTags = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'span' | 'p' | 'label' | 'a';
export const tagList: TextTags[] = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'p', 'label', 'a'];

const LINK_TAG: TextTags = 'a';
const LABEL_TAG: TextTags = 'label';

export interface IText extends IComponent {
  forId?: string;
  href?: string;
  tag?: TextTags;
  role?: string;
  tabIndex?: number;
  disabled?: boolean;
}

const classNames = ({ color, fontSize, tag, fontWeight }: IClassNames): IClassNames =>
  makeClassNames({
    text: [
      'text',
      `text--${tag}`,
      color && `text--color-${color}`,
      fontSize && `text--font-size-${fontSize}`,
      fontWeight && `text--weight-${fontWeight}`,
    ],
  });

const Text: React.FC<IText> = ({
  /* istanbul ignore next: default value */ tag = 'span',
  /* istanbul ignore next: default value */ dataAttributes = {},
  /* istanbul ignore next: default value */ className = '',
  children,
  color,
  fontSize,
  forId,
  href,
  fontWeight,
  role,
  tabIndex,
  disabled,
}) => {
  const Tag = tag as keyof JSX.IntrinsicElements;
  const cn = classNames({ color, fontSize, tag, fontWeight });
  const props: { htmlFor?: string; href?: string } = {};
  if (tag === LABEL_TAG && forId) {
    props.htmlFor = forId;
  }
  if (tag === LINK_TAG && href) {
    props.href = href;
  }
  return (
    <Tag
      className={`${cn.text} ${className}`}
      {...props}
      {...objectToDataAttributes(dataAttributes)}
      role={role}
      tabIndex={tabIndex}
      aria-disabled={disabled}
    >
      {/* @ts-ignore */}
      {children}
    </Tag>
  );
};

/* istanbul ignore else */ if (process.env.NODE_ENV !== 'production') {
  Text.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    color: PropTypes.oneOf(colorList),
    dataAttributes: PropTypes.object,
    fontSize: PropTypes.oneOf(fontSizeList),
    fontWeight: PropTypes.oneOf(fontWeightList),
    forId: PropTypes.string,
    href: PropTypes.string,
    tag: PropTypes.oneOf(tagList),
  };
}

export default Text;
